/* eslint-disable complexity */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import Loading from '../../components/Loading/Loading.jsx';
import Voucher from '../../components/Voucher/Voucher.jsx';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import StockChecker from '../../components/StockCheck/StockChecker.jsx';
import ProductVoucher from '../../components/ProductVoucher/ProductVoucher.jsx';
import Slider from '../../components/Slider/Slider.jsx';
import VendorHeader from '../../components/Vendor/Header/VendorHeader.jsx';
import VouchersDisplay from '../../components/Vouchers/VouchersDisplay.jsx';
import { EligibilityContext } from '../../services/context/EligibilityContext.js';
import ROUTES from '../../services/Constants/GlobalRoutes.jsx';
import AdvanceDateDetails from '../../components/AdvanceDateDetails/AdvanceDateDetails.jsx';
import {
  CategoriesCompleteContext,
  CategoriesContext,
} from '../../services/context/CategoriesContext.js';
import {
  defaultAnalyticsVariables,
  events,
  pagePrefix,
} from '../../services/Constants/Analytics.js';
import { AnalyticsPageContext } from '../../services/context/AnalyticsPageContext.js';
import {
  getVendorCategory,
  getVoucherVendor,
} from '../../services/ReverseHierarchySearch.js';
import { CartContext } from '../../services/context/CartContext.js';
import CheckoutButton from '../../components/CheckoutButton/CheckoutButton.jsx';
import StockStatus from '../../services/Constants/StockStatus.js';
import Redirect from '../../components/Redirect/Redirect.jsx';
import { withProfiler } from '@sentry/react';
import { useParams } from 'react-router-dom';
import { useCostSort } from '../../services/hooks/useCostSort.js';
import TopBack from '../../components/TopBack/TopBack.jsx';
import {
  AdvanceContext,
  UpdateAdvanceContext,
} from '../../services/context/AdvanceContext.js';
import { ConfigContext } from '../../services/context/ConfigContext.js';
import InfoIcon from '../../assets/InfoIcon.jsx';
import useQuery from '../../services/hooks/useQuery.js';
import { createIsAdvanceOptionFilter } from '../../services/EligibilityOffers.js';
import { SubscriptionDetailsContext } from '../../services/context/SubscriptionDetailsContext.js';
import VoucherExhaustedView from '../../components/VoucherExhaustedView/VoucherExhaustedView.jsx';
import { LimitsContext } from '../../services/context/LimitsContext.js';
import { BearerTokenContext } from '../../services/context/BearerTokenContext.js';
import './VendorPage.css';
import { getVoucherLimitInfo, isNandosVoucherLimitReached, hasEligibleVoucher }
  from '../../services/utils/vendorLimits.js';

/**
 * @returns {JSX.Element} - Vendor page component
 */
function VendorPage() {
  const { id: idParam } = useParams();
  const queryParams = useQuery();
  const vendorId = Number(idParam);
  const queryVoucherId = queryParams.get('voucher');
  const queryAdvanceId = queryParams.get('advance');
  const [currentVoucher, setCurrentVoucher] = useState();
  const [currentAdvance, setCurrentAdvance] = useState();
  const [advancing, setAdvancing] = [
    useContext(AdvanceContext),
    useContext(UpdateAdvanceContext),
  ];
  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const categoriesComplete = useContext(CategoriesCompleteContext);

  const cart = useContext(CartContext);
  const config = useContext(ConfigContext);
  const { costSort } = useCostSort();
  const MoneyFormatter = useContext(MoneyFormatterContext);
  const eligibility = useContext(EligibilityContext);
  const categories = useContext(CategoriesContext);
  const vendors = categories
    .filter(c => !c.isClubCategory)
    .flatMap(category => category.vendors);
  const vendor = vendors.find(value => value.id === vendorId);
  let campaignVouchers = null;
  const isEmpty = !vendor;
  const seen = new Set();
  const limits = useContext(LimitsContext);
  const bearerToken = useContext(BearerTokenContext);

  const allClubVouchersExhausted = useMemo(() => {
    if (!vendor?.name) return false;
    return hasEligibleVoucher(vendor, limits, cart, config);
  }, [
    vendor?.name,
    limits,
    cart,
    config,
    bearerToken,
  ]);

  /**
   * @param categories
   */
  function getAllCampaignVouchers(categories) {
    return categories.filter(category => !category.isClubCategory).reduce((mergedVendors, category) => [
      ...mergedVendors,
      ...category.vendors,
    ], []).reduce((mergedVouchers, nextVendor) => {
      const vouchersWithVendorInfo = nextVendor.vouchers.map(v => ({
        ...v,
        vendorName: nextVendor.name,
        vendorIconUrl: nextVendor.iconUrl,
      }));
      return [
        ...mergedVouchers,
        ...vouchersWithVendorInfo,
      ];
    }, []).filter(voucher => Boolean(voucher.campaign));
  }

  /**
   * Get hot deals vouchers
   */
  function getHotDealsVouchers() {
    const campaignIds = localStorage.getItem('campaignIds') || [];
    if (!campaignVouchers && categories && categoriesComplete) {
      campaignVouchers = getAllCampaignVouchers(categories)
        .filter(voucher => voucher.campaign && campaignIds.includes((voucher.campaign).toString()));
    }

    return campaignVouchers?.map(item => item.id) ?? [];
  }

  const sortVouchersForSubscribedUser = (vendorObject, isVirtual) => {
    // Filtering campaign/hot deals vouchers
    const campaignVouchersIds = getHotDealsVouchers();
    const nonHotDealsVouchers = vendor?.vouchers.filter(item => !campaignVouchersIds.includes(item.id)
                                && item.campaign === null);

    const clubCategory = categories
      .filter(category => category?.isClubCategory)
      .some(category => category?.vendors?.some(vendorObj => vendorObj.id === vendorObject.id));

    const matchesType = voucher => voucher?.stock?.type === (isVirtual ? 'virtual' : 'simple');
    const isClubVoucher = voucher => voucher?.promotionTag?.toString() === config.frontend.general.subscriptionPromotionTag.id
                           && voucher?.campaign === null;

    if (clubCategory) {
      const vodapayClubVouchers = nonHotDealsVouchers?.filter(voucher => matchesType(voucher) && isClubVoucher(voucher));
      const nonVodapayClubVouchers = nonHotDealsVouchers?.filter(voucher => matchesType(voucher) && !isClubVoucher(voucher));

      // To set a flag for club limit feature
      localStorage.setItem('clubVoucherPresent', vodapayClubVouchers?.length > 0);
      if (!subscriptionDetails?.isSubscribed) {
        return [
          ...(nonVodapayClubVouchers ? nonVodapayClubVouchers.sort(costSort) : []),
          (vodapayClubVouchers ? vodapayClubVouchers.sort((a, b) => costSort(a, b, true))[0] : undefined),
        ];
      }

      if (allClubVouchersExhausted) {
        const vouchers = nonVodapayClubVouchers || [];
        return [...vouchers].sort(costSort);
      }

      return vodapayClubVouchers?.sort(costSort);
    }

    return nonHotDealsVouchers?.sort(costSort);
  };

  const sortVendorsArrayForSubscribedUser = vendorObject => sortVouchersForSubscribedUser(vendorObject, true);
  const sortProductVouchersForSubscribedUser = vendorObject => sortVouchersForSubscribedUser(vendorObject, false);

  useEffect(() => {
    if (!isEmpty && queryVoucherId) setCurrentVoucher(vendor?.vouchers.find(voucher => voucher.id === queryVoucherId));
  }, [vendor]);

  useEffect(() => {
    if (!isEmpty && eligibility?.tcl && queryAdvanceId) setCurrentAdvance(vendor?.vouchers.find(voucher => voucher.id === queryAdvanceId));
  }, [
    vendor,
    eligibility,
  ]);

  const skeletonVouchers = [
    0,
    1,
    2,
  ];

  const isAdvance = voucher => {
    const isAdvanceOption = createIsAdvanceOptionFilter(
      eligibility,
      voucher,
      MoneyFormatter,
    );
    return Boolean(voucher?.options?.advance.terms.options?.some(option => isAdvanceOption(option)));
  };

  const isBuyNow = voucher => Boolean(voucher?.options?.buyNow.payment.code);

  const analyticsName = `${pagePrefix}: vendor: ${vendor?.name}`;
  useEffect(() => {
    if (vendor) {
      window.utag?.view({
        ...defaultAnalyticsVariables,
        page_name: analyticsName,
        event_name: [
          events.pageView,
          events.productView,
        ],
        product_category: vendor.vouchers?.map(() => getVendorCategory(categories, vendor)?.name),
        product_quantity: vendor.vouchers?.map(() => 1),
        product_name: vendor.vouchers?.map(voucher => voucher.name),
        product_price: vendor.vouchers?.map(voucher => MoneyFormatter.getValue(voucher.cost).amount),
        product_id: vendor.vouchers?.map(voucher => voucher.id),
      });
    }
  }, []);

  if (!categoriesComplete && isEmpty) {
    return <Loading />;
  } else if (isEmpty && categoriesComplete) {
    return <Redirect to={ ROUTES.HOME } />;
  }

  const handleAdvance = voucher => {
    setAdvancing({
      voucher,
      date: null,
    });
    window.utag?.link({
      ...defaultAnalyticsVariables,
      page_name: analyticsName,
      event_name: [events.interaction],
      link_id: `${pagePrefix}: advance now: ${vendor.name}`,
      product_category: getVendorCategory(
        categories,
        getVoucherVendor(categories, voucher),
      ).name,
      product_name: voucher.name,
      product_price: MoneyFormatter.getValue(voucher.cost).amount,
      product_id: voucher.id,
      product_quantity: 1,
    });
  };

  const getAdvanceActionItem = voucher => {
    if (config.frontend.general.maintenanceWarning.flows.advance) {
      return (
        <p className='warning container-vh'>
          <InfoIcon /> Currently unavailable
        </p>
      );
    }

    return voucher.stock.status === StockStatus.inStock ? (
      <button
        disabled={ config.frontend.general.maintenanceWarning.flows.advance }
        className='secondary-button'
        onClick={ () => handleAdvance(voucher) }
      >
        Advance now
      </button>
    ) : (
      <>Out of stock</>
    );
  };

  const closeAdvance = () => {
    setAdvancing(null);
    window.utag?.link({
      ...defaultAnalyticsVariables,
      page_name: `${pagePrefix}: payment due date selection`,
      event_name: [events.interaction],
    });
  };

  const { vouchers, products, advances } = !vendor?.vouchers
    ? {
      vouchers: [],
      products: [],
      advances: [],
    }
    : {
      vouchers: sortVendorsArrayForSubscribedUser(vendor)
        .filter(value => isBuyNow(value))
        .map(voucher => (
          <Voucher
            key={ voucher.id }
            title={
              <>
                {`Pay ${MoneyFormatter.format(MoneyFormatter.getValue(voucher?.cost))} get `}
                <em>
                  {MoneyFormatter.format(MoneyFormatter.getValue(voucher?.options.buyNow.value))}
                </em>
              </>
            }
            addedValue={
              `Get ${MoneyFormatter.format(MoneyFormatter.subtract(
                MoneyFormatter.getValue(voucher?.cost),
                MoneyFormatter.getValue(voucher?.options.buyNow.value),
              ))} extra`
            }
            actionItem={
              <StockChecker
                voucher={ voucher }
                isClubVoucher={
                  voucher?.promotionTag?.toString()
                  === config.frontend.general.subscriptionPromotionTag.id
                }
                vouchers={ vendor.vouchers }
              />
            }
            onClick={ () => setCurrentVoucher(voucher) }
            isClubVoucher={
              voucher.promotionTag?.toString()
              === config.frontend.general.subscriptionPromotionTag.id
            }
            voucherId={ voucher.id }
            voucher={ voucher }
          />
        )),
      products: sortProductVouchersForSubscribedUser(vendor)
        .filter(value => isBuyNow(value))
        .filter(value => value.stock?.type === 'simple')
        .map(voucher => (
          <ProductVoucher
            descriptionType='buyNow'
            key={ `${voucher.id}_product` }
            voucher={ voucher }
            addedValue={
              `Get ${MoneyFormatter.format(MoneyFormatter.subtract(
                MoneyFormatter.getValue(voucher?.cost),
                MoneyFormatter.getValue(voucher?.options.buyNow.value),
              ))
              } extra`
            }
            actionItem={
              <StockChecker
                voucher={ voucher }
                isClubVoucher={ voucher.promotionTag?.toString() === config.frontend.general.subscriptionPromotionTag.id }
              />
            }
            iconUrl={ getVoucherVendor(categories, voucher)?.iconUrl }
            isClubVoucher={ voucher.promotionTag?.toString() === config.frontend.general.subscriptionPromotionTag.id }
          />
        )),
      advances: vendor?.vouchers
        .filter(value => isAdvance(value))
        .filter(voucher => eligibility?.tcl && MoneyFormatter.getValue(voucher.cost).amount <= eligibility.tcl.amount)
        .filter(voucher => {
          const price = voucher.cost?.ZAR;
          if (seen.has(price)) return false;
          seen.add(price);
          return true;
        })
        .sort(costSort)
        .map(voucher => (
          <Voucher
            title={
              <>
                Get
                {' '}
                <em>
                  {MoneyFormatter.format(MoneyFormatter.getValue(voucher?.options.advance.value))}
                </em>
              </>
            }
            addedValue={ <>Pay later</> }
            actionItem={ getAdvanceActionItem(voucher) }
            key={ voucher.id }
            onClick={ () => setCurrentAdvance(voucher) }
            voucher={ voucher }
            isAdvance={ true }
          />
        )),
    };

  const displayAdvanceDetails = () => currentAdvance && (
    <Slider
      onClickOutside={ () => setCurrentAdvance(null) }
      slot={
        <ProductVoucher
          descriptionType='advance'
          voucher={ currentAdvance }
          actionItem={ getAdvanceActionItem(currentAdvance) }
        />
      }
    />
  );

  const displayAdvanceDateSelection = () => advancing && (
    <Slider
      onClickOutside={ closeAdvance }
      slot={ <AdvanceDateDetails /> }
    />
  );

  const displayVoucherDetails = () => currentVoucher && (
    <Slider
      onClickOutside={ () => setCurrentVoucher(null) }
      slot={
        <ProductVoucher
          descriptionType='buyNow'
          voucher={ currentVoucher }
          actionItem={
            <StockChecker
              voucher={ currentVoucher }
              isClubVoucher={
                currentVoucher.promotionTag?.toString()
                === config.frontend.general.subscriptionPromotionTag.id
              }
            />
          }
        />
      }
    />
  );

  // Raw data arrays for stock checking (separate from JSX render arrays)
  const rawVoucherData = Array.isArray(vendors) ? sortVendorsArrayForSubscribedUser(vendor)?.filter(v => isBuyNow(v)) : [];
  const rawProductData = Array.isArray(vendors) ? sortProductVouchersForSubscribedUser(vendor) : []
    ?.filter(v => isBuyNow(v))
    ?.filter(v => v.stock?.type === 'simple');

  const isVoucherOutOfStock = (voucher, cart, limits) => {
    const isAdvance = createIsAdvanceOptionFilter(eligibility, voucher, MoneyFormatter);
    const { isPurchased } = getVoucherLimitInfo(limits, voucher?.id, cart, isAdvance);

    return (voucher?.stock?.status !== StockStatus.inStock) || isPurchased;
  };

  // Check all vouchers are out of stock..
  const allVouchersOutOfStock = rawVoucherData.every(v => isVoucherOutOfStock(v, cart, limits));

  // Check all product vouchers are out of stock..
  const allProductsOutOfStock = rawProductData.every(v => isVoucherOutOfStock(v, cart, limits));

  // Check if vendor is Nando's and user exhausted its limit
  const isNandosVendor = vendor?.name?.toLowerCase().includes('nando');
  const hasNandos = isNandosVendor && limits?.some(item => item.description.toLowerCase().includes('nando'));

  const limitExhaustedForNandos = isNandosVoucherLimitReached(vendor, limits, cart);
  const showCrossSellForNandos = hasNandos && limitExhaustedForNandos;
  const showCrossSellComponent = (allVouchersOutOfStock && allProductsOutOfStock) || showCrossSellForNandos;

  return (
    <AnalyticsPageContext.Provider value={ analyticsName }>
      <TopBack />
      <VendorHeader
        vendor={ vendor }
        showCrossSellComponent={ showCrossSellComponent }
        allClubVouchersExhausted={ allClubVouchersExhausted }
      />

      {
        showCrossSellComponent ? (
          <VoucherExhaustedView />
        ) : (
          <>
            {
              eligibility?.tcl && (
                <>
                  <VouchersDisplay
                    title={ <h4>Buy now, pay later</h4> }
                    onLoading={
                      <>
                        {
                          skeletonVouchers.map(number => (
                            <Voucher key={ number } />
                          ))
                        }
                      </>
                    }
                    onEmpty={
                      isEmpty ? null : (
                        <p>
                          Uh oh! looks like there aren&apos;t any advances for{' '}
                          {vendor.name} at the moment. Check back later
                        </p>
                      )
                    }
                    isLoading={ !categoriesComplete }
                    vouchers={ advances }
                  />
                  {
                    (!allVouchersOutOfStock || !allProductsOutOfStock) && (
                      <div className='vendor-page-divider'>
                        <span />
                        <p>or</p>
                        <span />
                      </div>
                    )
                  }
                </>
              )
            }

            {
              (!allVouchersOutOfStock || !allProductsOutOfStock) && (
                <VouchersDisplay
                  title={ <h4>Buy now, get more</h4> }
                  onLoading={
                    <>
                      {
                        skeletonVouchers.map(number => (
                          <Voucher key={ number } />
                        ))
                      }
                    </>
                  }
                  onEmpty={
                    isEmpty ? null : (
                      <p>
                        Uh oh! looks like there aren&apos;t any vouchers for {vendor.name}{' '}
                        at the moment. Check back later
                      </p>
                    )
                  }
                  isLoading={ !categoriesComplete }
                  vouchers={
                    [
                      ...vouchers,
                      ...products,
                    ]
                  }
                />
              )
            }

            {displayVoucherDetails()}
            {displayAdvanceDetails()}
            {displayAdvanceDateSelection()}
            {cart.length > 0 && <CheckoutButton />}
          </>
        )
      }
    </AnalyticsPageContext.Provider>
  );
}

export default withProfiler(VendorPage);
